import { useEffect, useState, type FormEvent } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  HelpTip,
  Icon,
  Input,
  cn,
  formatDateTime,
  formatTime,
} from '@dataroom/shared';
import type { AuditLogEntry } from '@dataroom/shared';
import {
  ACTIVITY_GROUP_LABELS,
  EMPTY_ACTIVITY_SEARCH,
  SEARCH_FIELD_LABELS,
  SYSTEM_ACTOR_LABEL,
  activityGroup,
  groupActivityByDay,
  isSearching,
  type ActivityGroup,
  type ActivityItem,
  type ActivitySearch,
  type SearchField,
} from '@/lib/activity';

const FIELD_CLASS =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50';

interface AuditLogCardProps {
  entries: AuditLogEntry[];
  loading: boolean;
  error: string | null;
  /** An older page of entries exists (the feed loads a page at a time). */
  hasOlder?: boolean;
  loadingOlder?: boolean;
  onLoadOlder?: () => void;
  /** What is being searched for / shown right now (the server already applied it). */
  search?: ActivitySearch;
  /** The person searched, picked dates, or flipped "Show views and system activity". */
  onSearchChange?: (search: ActivitySearch) => void;
}

type Filter = 'all' | ActivityGroup;
const FILTERS: Filter[] = ['all', 'documents', 'people', 'agreements', 'tracking'];

/**
 * The room's activity — its tamper-evident, append-only audit log, read-only for
 * everyone (including the admin) — as a plain-language feed grouped by day:
 * "deals@northbridge.example submitted a letter of intent · 4:51 PM", each entry
 * naming the document, checklist item, milestone, folder or person it is about. By
 * default it shows what PEOPLE did; views and automatic activity come back with
 * "Show views and system activity". Search (person, what happened, document,
 * checklist item, milestone, folder, dates) is answered by the server. The
 * integrity detail (entry number, record, SHA-256 chain hash) sits behind each
 * entry's "Details" disclosure instead of raw types, UUIDs and hashes in the row.
 */
export function AuditLogCard({
  entries,
  loading,
  error,
  hasOlder = false,
  loadingOlder = false,
  onLoadOlder,
  search = EMPTY_ACTIVITY_SEARCH,
  onSearchChange,
}: AuditLogCardProps) {
  const [filter, setFilter] = useState<Filter>('all');
  const [draft, setDraft] = useState<ActivitySearch>(search);
  // The applied search changed from outside (e.g. cleared): the form follows it.
  useEffect(() => setDraft(search), [search]);
  const apply = (next: ActivitySearch) => onSearchChange?.(next);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    apply(draft);
  };
  const searching = isSearching(search);
  const matches = (f: Filter) => (e: AuditLogEntry) => f === 'all' || activityGroup(e.action) === f;
  const shown = entries.filter(matches(filter));
  const days = groupActivityByDay(shown);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Activity</CardTitle>
        <p className="text-sm text-muted-foreground">
          What people did in this room, newest first. Entries can’t be edited or deleted in the app,
          and each one is chained to the last, so any change is detectable.
        </p>
        <form
          role="search"
          aria-label="Search activity"
          onSubmit={submit}
          className="grid gap-3 pt-2 md:grid-cols-[minmax(0,1fr)_180px]"
        >
          <div className="space-y-1.5">
            <label htmlFor="activity-search-text" className="text-sm font-medium">
              Search
            </label>
            <Input
              id="activity-search-text"
              type="search"
              placeholder="A name, a file, a milestone…"
              value={draft.text}
              onChange={(e) => setDraft({ ...draft, text: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="activity-search-field" className="text-sm font-medium">
              Look in
            </label>
            <select
              id="activity-search-field"
              className={FIELD_CLASS}
              value={draft.field}
              onChange={(e) => setDraft({ ...draft, field: e.target.value as SearchField })}
            >
              {(Object.keys(SEARCH_FIELD_LABELS) as SearchField[]).map((f) => (
                <option key={f} value={f}>
                  {SEARCH_FIELD_LABELS[f]}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3 md:col-span-2 md:grid-cols-[180px_180px_1fr]">
            <div className="space-y-1.5">
              <label htmlFor="activity-search-from" className="text-sm font-medium">
                From
              </label>
              <input
                id="activity-search-from"
                type="date"
                className={FIELD_CLASS}
                value={draft.from}
                max={draft.to || undefined}
                onChange={(e) => setDraft({ ...draft, from: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="activity-search-to" className="text-sm font-medium">
                To
              </label>
              <input
                id="activity-search-to"
                type="date"
                className={FIELD_CLASS}
                value={draft.to}
                min={draft.from || undefined}
                onChange={(e) => setDraft({ ...draft, to: e.target.value })}
              />
            </div>
            <div className="col-span-2 flex items-end gap-2 md:col-span-1">
              <Button type="submit" size="sm">
                Search
              </Button>
              {searching || draft.text !== '' || draft.from !== '' || draft.to !== '' ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => apply({ ...EMPTY_ACTIVITY_SEARCH, showAll: search.showAll })}
                >
                  Clear
                </Button>
              ) : null}
            </div>
          </div>
        </form>
        <div className="flex flex-wrap items-center gap-1.5 pt-1">
          <label className="flex w-fit items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={search.showAll}
              onChange={(e) => apply({ ...search, showAll: e.target.checked })}
            />
            Show views and system activity
          </label>
          <HelpTip id="admin.activity.show-all" />
        </div>
        <div role="group" aria-label="Show" className="flex flex-wrap gap-2 pt-2">
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              aria-pressed={filter === f}
              onClick={() => setFilter(f)}
              className={cn(
                'inline-flex h-8 items-center gap-1.5 rounded-pill border px-3 text-[13px] font-medium transition-colors max-md:min-h-tap',
                filter === f
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-input bg-surface text-ink-2 hover:bg-surface-2',
              )}
            >
              {f === 'all' ? 'All' : ACTIVITY_GROUP_LABELS[f]}
              <span
                className={cn(
                  'rounded-pill px-1.5 text-[11px] font-semibold',
                  filter === f ? 'bg-primary-foreground text-primary' : 'bg-surface-2 text-ink-2',
                )}
              >
                {entries.filter(matches(f)).length}
              </span>
            </button>
          ))}
        </div>
        <p className="sr-only" aria-live="polite">
          {`${shown.length} ${shown.length === 1 ? 'entry' : 'entries'} shown`}
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : error ? (
          <p className="text-sm text-destructive">{error}</p>
        ) : days.length > 0 ? (
          <div className="space-y-2">
            {days.map((day) => (
              <section key={day.label} aria-label={day.label}>
                <h3 className="pb-2 pt-3 text-xs font-semibold text-ink-3">{day.label}</h3>
                <ul>
                  {day.items.map((item) => (
                    <FeedItem key={item.entry.id} item={item} />
                  ))}
                </ul>
              </section>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            {entries.length > 0
              ? 'Nothing of this kind yet.'
              : searching
                ? 'Nothing matches that search.'
                : search.showAll
                  ? 'No activity in this room yet.'
                  : 'No activity from people in this room yet.'}
            {entries.length === 0 && !search.showAll ? (
              <>
                {' '}
                Views and automatic activity are hidden.{' '}
                <button
                  type="button"
                  className="rounded text-primary-ink underline underline-offset-2"
                  onClick={() => apply({ ...search, showAll: true })}
                >
                  Show views and system activity
                </button>
              </>
            ) : null}
          </p>
        )}
        {!loading && !error && hasOlder && onLoadOlder ? (
          <div className="flex items-center gap-3 border-t pt-3">
            <Button variant="outline" size="sm" disabled={loadingOlder} onClick={onLoadOlder}>
              {loadingOlder ? 'Loading…' : 'Load older activity'}
            </Button>
            <span className="text-xs text-muted-foreground">
              {`Showing the latest ${entries.length} entries.`}
            </span>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function FeedItem({ item }: { item: ActivityItem }) {
  const { entry } = item;
  return (
    <li className="grid grid-cols-[32px_minmax(0,1fr)_auto] items-start gap-3 border-t py-3">
      <span
        aria-hidden="true"
        className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-surface-2 text-ink-2"
      >
        <Icon name={item.icon} />
      </span>
      <div className="min-w-0">
        <p className="break-words text-sm">
          <strong className="font-semibold">{item.actor ?? SYSTEM_ACTOR_LABEL}</strong> {item.phrase}
        </p>
        {item.objects.length > 0 ? (
          <ul className="mt-0.5 space-y-0.5 text-sm text-ink-2">
            {item.objects.map((o) => (
              <li key={o.label} className="break-words">
                <span className="text-ink-3">{o.label}:</span>{' '}
                {o.deleted ? (
                  <span className="italic text-ink-3">(deleted)</span>
                ) : o.name ? (
                  <span className="font-medium text-ink">{o.name}</span>
                ) : (
                  <span className="italic text-ink-3">(unnamed)</span>
                )}
              </li>
            ))}
          </ul>
        ) : null}
        <details className="mt-1">
          <summary className="w-fit cursor-pointer list-none rounded text-xs font-medium text-primary-ink underline-offset-2 hover:underline [&::-webkit-details-marker]:hidden">
            Details
          </summary>
          <div className="mt-2 space-y-1.5 rounded-md border bg-surface-2 p-3 text-xs text-ink-2">
            <p>
              This entry is sealed into the room’s tamper-evident log. Every later entry depends on
              it, so any change would show.
            </p>
            <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1">
              <dt className="text-ink-3">When</dt>
              <dd>{formatDateTime(entry.createdAt)}</dd>
              <dt className="text-ink-3">Entry</dt>
              <dd className="tabular-nums">#{entry.id}</dd>
              <dt className="text-ink-3">Record</dt>
              <dd className="break-all">
                {entry.targetType} · {entry.targetId}
              </dd>
              <dt className="text-ink-3">SHA-256</dt>
              <dd className="break-all font-mono text-ink">{entry.entryHash}</dd>
            </dl>
          </div>
        </details>
      </div>
      <time dateTime={entry.createdAt} className="whitespace-nowrap pt-0.5 text-xs text-ink-3">
        {formatTime(entry.createdAt)}
      </time>
    </li>
  );
}
